import { useState, type MouseEvent, type ReactNode } from 'react';
import { Box, IconButton, Popover, Stack, Typography } from '@mui/material';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * In-place help (medopd-docs 97). A small (i) next to a label; clicking it explains what the field or section is
 * for, in the person's language. Text comes from the `help` namespace: `help:<pageKey>.fields.<field>`.
 *
 * - {@link HelpTip}: the icon alone — put it after a heading, column title, switch or tab label.
 * - {@link LabelWithHelp}: a label with the icon, for FormControlLabel / section titles.
 * - {@link FieldWithHelp}: wraps any input (TextField, Select, Autocomplete, toggle group) with the icon beside it.
 *
 * The icon never takes focus or clicks away from the control it sits in (a label click would otherwise focus the
 * input or flip the checkbox).
 */
export function HelpTip({ helpKey, text, title, size = 15 }: {
  /** `<pageKey>.fields.<field>` in the help namespace. */
  helpKey?: string;
  /** Already-translated text, when not using a key. */
  text?: ReactNode;
  /** Heading shown above the text (usually the field's label). */
  title?: ReactNode;
  size?: number;
}) {
  const { t, i18n } = useTranslation('help');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // No text, no icon: an (i) that opens nothing is worse than none.
  const body = text ?? (helpKey && i18n.exists(helpKey, { ns: 'help' }) ? t(helpKey) : '');
  if (!body) return null;
  const label = typeof title === 'string' ? title : '';

  const stop = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <>
      <IconButton
        size="small"
        aria-label={label ? t('aboutField', { field: label, defaultValue: 'About {{field}}' }) : t('moreInfo', { defaultValue: 'More information' })}
        onMouseDown={stop}
        onClick={(e) => { stop(e); setAnchor(e.currentTarget); }}
        sx={{ p: 0.25, ml: 0.5, color: 'text.secondary', verticalAlign: 'middle', '&:hover': { color: 'primary.main' } }}
      >
        <Info size={size} />
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 340, p: 1.75, borderRadius: 2 } } }}
      >
        <Stack spacing={0.75}>
          {title && <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}>{body}</Typography>
        </Stack>
      </Popover>
    </>
  );
}

/** A label followed by its (i). Use as a FormControlLabel / Tab label, or as a section title's content. */
export function LabelWithHelp({ label, helpKey, text }: { label: ReactNode; helpKey?: string; text?: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {label}
      <HelpTip helpKey={helpKey} text={text} title={label} />
    </Box>
  );
}

/** Any input with its (i) beside it, aligned to the input box (not the helper text below it). */
export function FieldWithHelp({ helpKey, text, title, children, fullWidth = true }: {
  helpKey?: string;
  text?: ReactNode;
  title?: ReactNode;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    // Not full width: hug the input (even inside a column Stack, which would otherwise stretch the row and push
    // the (i) to the far edge, away from the field it explains).
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.25, minWidth: 0,
      ...(fullWidth ? { width: '100%' } : { width: 'fit-content', maxWidth: '100%', alignSelf: 'flex-start' }),
    }}>
      <Box sx={{ flex: fullWidth ? 1 : '0 1 auto', minWidth: 0 }}>{children}</Box>
      <Box sx={{ pt: 1 }}><HelpTip helpKey={helpKey} text={text} title={title} /></Box>
    </Box>
  );
}
